import type { PayloadAction } from '@reduxjs/toolkit';

import FilterRoot from './Filter.Root';
import FilterOverview from './Filter.Overview';
import FilterFromContractNumbers from './Filter.Form.ContractNumbers';
import FilterFormPeriod from './Filter.Form.Period';
import FilterFormProducts from './Filter.Form.Products';
import FilterFormCategories from './Filter.Form.Categories';
import { bankColumns, mainColumns } from '../Document/Document.columns';

// extract the names of sortable columns from a config array
type SortableColumnNames<T> = T extends readonly [infer Head, ...infer Tail]
  ? Head extends { name: string; sortable: true }
    ? Head['name'] | SortableColumnNames<Tail>
    : SortableColumnNames<Tail>
  : never;

// sortable column names from every segment
export type SortableColumns =
  | SortableColumnNames<typeof mainColumns>
  | SortableColumnNames<typeof bankColumns>;

interface Filter {
  page: Filter.Page;
  size: Filter.Size;
  sortBy?: Filter.SortBy;
  sortDirection?: Filter.SortDirection;
  contractNumbers?: Filter.ContractNumber[];
  products?: Filter.Product[];
  categories?: Filter.Category[];
  createdFrom?: Filter.CreatedFrom;
  createdTo?: Filter.CreatedTo;
  segment: Filter.Segment;
}

interface CategoryMapping {
  idToSlug: Record<number, string>;
  slugToId: Record<string, number>;
  isReady: boolean;
  pendingCategorySlugs: string[] | null;
}

class ChangeCategoriesAction {}

// eslint-disable-next-line @typescript-eslint/no-namespace
namespace Filter {
  export const Root = FilterRoot;
  export const Overview = FilterOverview;

  // eslint-disable-next-line @typescript-eslint/no-namespace
  export namespace Form {
    export const Period = FilterFormPeriod;
    export const ContractNumbers = FilterFromContractNumbers;
    export const Products = FilterFormProducts;
    export const Categories = FilterFormCategories;
  }

  export type DateISO = string;

  export type Page = number;
  export type Size = number;
  export type Segment = string;
  export type ContractNumber = string;
  export type Product = string;
  export type Category = number;
  export type CreatedFrom = DateISO;
  export type CreatedTo = DateISO;
  export type SortDirection = 'ASC' | 'DESC';
  export type SortBy = SortableColumns;

  export type ReducerState = Filter;
  export type CategoryMappingState = CategoryMapping;

  export type ChangePageAction = PayloadAction<Page>;
  export type ChangeSortByAction = PayloadAction<SortBy>;
  export type ChangeContractNumbersAction = PayloadAction<ContractNumber[]>;
  export type DeselectContractNumberAction = PayloadAction<ContractNumber>;
  export type ChangeProductsAction = PayloadAction<Product[]>;
  export type DeselectProductAction = PayloadAction<Product>;
  export type ChangeCategoryAction = PayloadAction<Category[]>;
  export type DeselectCategoryAction = PayloadAction<Category>;
  export type ChangeCreatedFromAction = PayloadAction<DateISO>;
  export type ChangeCreatedToAction = PayloadAction<DateISO>;
  export type ChangeSegmentAction = PayloadAction<Segment>;
  export type SetStateAction = PayloadAction<Partial<Filter>>;

  // eslint-disable-next-line @typescript-eslint/no-namespace
  export namespace Action {
    export type ChangePage = ChangePageAction;
    export type ChangeSortBy = ChangeSortByAction;
    export type ChangeContractNumbers = ChangeContractNumbersAction;
    export type DeselectContractNumber = DeselectContractNumberAction;
    export type ChangeProducts = ChangeProductsAction;
    export type DeselectProduct = DeselectProductAction;
    export type ChangeCategories = ChangeCategoriesAction;
    export type DeselectCategory = DeselectCategoryAction;
    export type ChangeCreatedFrom = ChangeCreatedFromAction;
    export type ChangeCreatedTo = ChangeCreatedToAction;
    export type ChangeSegment = ChangeSegmentAction;
    export type SetState = SetStateAction;
  }

  export type Action =
    | ChangePageAction
    | ChangeSortByAction
    | ChangeContractNumbersAction
    | DeselectContractNumberAction
    | ChangeProductsAction
    | DeselectProductAction
    | ChangeCategoriesAction
    | DeselectCategoryAction
    | ChangeCreatedFromAction
    | ChangeCreatedToAction
    | ChangeSegmentAction
    | SetStateAction;
}

export default Filter;
